import {
  ApplicationConfig,
  computed,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import localeNb from '@angular/common/locales/nb';
import localeNn from '@angular/common/locales/nn';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { authInterceptor, provideAuth } from '@nabotavle/auth';
import { provideNbUiText, provideTheme, type NbUiText } from '@nabotavle/ui';
import { I18N_SUFFIX, LocaleService, resolveInitialLocale, toAngularLocale } from '@nabotavle/util';

import { routes } from './app.routes';

// Norwegian dates/numbers (DatePipe etc.).
// All three shipped locales, so DatePipe/number formatting follows the chosen language (LOCALE_ID is
// resolved once at bootstrap from the persisted choice; a switch applies fully from the next load).
registerLocaleData(localeNb);
registerLocaleData(localeNn);
registerLocaleData(localeEnGb);

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: toAngularLocale(resolveInitialLocale()) },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    // /api/styre/* calls carry the bearer token via the interceptor; the API enforces
    // the Styre (styremedlem/-leder) policy. Web-only console — no native token store.
    provideHttpClient(withInterceptors([authInterceptor])),
    // i18n (V1.12) — same runtime ngx-translate setup as kundeapp; locale JSON from /i18n/<lang>.json.
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: '/i18n/', suffix: I18N_SUFFIX }),
      fallbackLang: 'nb',
      lang: resolveInitialLocale(),
    }),
    provideAppInitializer(() => inject(LocaleService).init()),
    provideAuth({ apiBase: '/api' }),
    // Design-system copy (close buttons, search placeholders, palette chrome, trend words) follows the
    // chosen language, so a component's built-in label never falls back to Norwegian in nn/en.
    provideNbUiText(() => {
      const t = inject(TranslateService);
      return computed(() => {
        t.currentLang();
        const ui = t.instant('ui') as Partial<NbUiText> | string;
        return typeof ui === 'object' ? ui : {};
      });
    }),
    provideTheme({ scheme: 'light' }),
  ],
};
